import type {
  FacilityLevelsOfCareDto,
  GetFacilityLevelsOfCareRequestDto,
} from '@/shared/api/modules/facilities';
import { cn } from '@/shared/lib/cn';
import { formatCurrency } from '@/shared/lib/currency';
import { useTableSortingState } from '@/shared/lib/hooks';
import { Indicator, Table, TableHeaderCell, Typography } from '@/shared/ui';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table';
import type { Dayjs } from 'dayjs';
import { useState, type FC } from 'react';
import { getFacilityLevelsOfCareQueryOptions } from '../api/get-facility-levels-of-care.query';

const columnHelper = createColumnHelper<FacilityLevelsOfCareDto>();

type FacilityLevelsOfCareTableProps = {
  /** Omit for the All Facilities view. */
  facilityId?: string;
  onRowHover: (levelOfCareData: FacilityLevelsOfCareDto) => void;
  onHoverEnd: () => void;
  dateRange: {
    from: Dayjs;
    to: Dayjs;
  };
};
export const levelsColors = [
  'bg-primary-6',
  'bg-volcano-6',
  'bg-polar-green-6',
  'bg-golden-purple-6',
  'bg-calendula-gold-6',
  'bg-dust-red-6',
];

/**
 * level 0 is the "Residents without Level of Care" row (card 230): residents with no completed
 * evaluation, whose revenue is not assigned to any level. It carries revenue only, so every other
 * column shows N/A for it.
 */
const NO_LEVEL_OF_CARE = 0;
const NO_LEVEL_OF_CARE_LABEL = 'Residents without Level of Care';
const isNoLevelRow = (row: FacilityLevelsOfCareDto) =>
  row.levelOfCare === NO_LEVEL_OF_CARE;

const columns = [
  columnHelper.accessor('levelOfCare', {
    enableSorting: true,
    header: 'Care Level',
    maxSize: 200,
    cell: ({ getValue }) => {
      const value = getValue();

      return (
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'h-1.5 w-1.5',
              value === NO_LEVEL_OF_CARE
                ? 'bg-neutral-7'
                : levelsColors[value - 1]
            )}
          />
          <Typography>
            {value === NO_LEVEL_OF_CARE
              ? NO_LEVEL_OF_CARE_LABEL
              : `Level of Care ${value}`}
          </Typography>
        </div>
      );
    },
    footer: (info) => info.column.id,
  }),
  columnHelper.accessor('createdFlags', {
    enableSorting: true,
    header: () => <TableHeaderCell subtitle="Sum" title="Created Flags" />,
    cell: ({ getValue, row }) => {
      if (isNoLevelRow(row.original)) return <Typography>N/A</Typography>;

      const value = getValue();

      return (
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'h-1.5 w-1.5 rounded-full',
              value > 25 ? 'bg-volcano-6' : 'bg-primary-6'
            )}
          />
          <Typography className="truncate text-[15px]">{value}</Typography>
        </div>
      );
    },
    footer: (info) => info.column.id,
  }),
  columnHelper.accessor('activeAlerts', {
    enableSorting: true,
    header: () => (
      <TableHeaderCell title="Active Alerts" subtitle="End of Period" />
    ),
    maxSize: 200,
    cell: ({ getValue, row }) => (
      <Typography noWrap>
        {isNoLevelRow(row.original) ? 'N/A' : (getValue() ?? 'N/A')}
      </Typography>
    ),

    footer: (info) => info.column.id,
  }),

  columnHelper.accessor('createdAlerts', {
    enableSorting: true,
    header: () => <TableHeaderCell subtitle="Sum" title="Created Alerts" />,
    cell: ({ getValue, row }) => (
      <Typography noWrap>
        {isNoLevelRow(row.original) ? 'N/A' : (getValue() ?? 'N/A')}
      </Typography>
    ),
    footer: (info) => info.column.id,
  }),
  columnHelper.accessor('residents', {
    enableSorting: true,
    header: () => (
      <TableHeaderCell subtitle="Avg" title="Number of Residents" />
    ),
    maxSize: 200,
    cell: ({ getValue, row }) => {
      if (isNoLevelRow(row.original)) return <Typography>N/A</Typography>;

      const { currentAvg, changePct } = getValue();

      return (
        <div className="flex gap-1">
          <Typography>{currentAvg ?? 'N/A'}</Typography>
          {changePct ? (
            <Indicator
              brackets
              noTooltip
              position="right"
              value={changePct}
              negativeValue={changePct < 0}
            />
          ) : null}
        </div>
      );
    },
    footer: (info) => info.column.id,
  }),
  columnHelper.accessor('totalRevenue', {
    enableSorting: true,
    header: () => <TableHeaderCell subtitle="Sum" title="Total Revenue" />,
    maxSize: 200,
    cell: ({ getValue }) => {
      const { currentAvg, changePct } = getValue();

      return (
        <div className="flex gap-1">
          <Typography>{formatCurrency(currentAvg, true) ?? 'N/A'}</Typography>
          {changePct ? (
            <Indicator
              brackets
              noTooltip
              position="right"
              value={changePct}
              negativeValue={changePct < 0}
            />
          ) : null}
        </div>
      );
    },
    footer: (info) => info.column.id,
  }),
] as ColumnDef<FacilityLevelsOfCareDto>[];

export const FacilityLevelsOfCareTable: FC<FacilityLevelsOfCareTableProps> = ({
  facilityId,
  onRowHover,
  onHoverEnd,
  dateRange,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  const { sortDirection, sortBy, sortingState, setSortingState } =
    useTableSortingState<
      NonNullable<GetFacilityLevelsOfCareRequestDto['sortBy']>
    >({
      sortBy: 'levelOfCare',
      sortDirection: 'asc',
    });

  const {
    data: levelsOfCare,
    isPending: isLevelsOfCareLoading,
    isError: isLevelsOfCareError,
  } = useQuery(
    getFacilityLevelsOfCareQueryOptions({
      sortBy,
      sortDirection,
      perPage: 10,
      page: currentPage,
      facilityId,
      from: dateRange.from.format('YYYY-MM-DD'),
      to: dateRange.to.format('YYYY-MM-DD'),
    })
  );

  return (
    <Table
      striped
      headerSeparated
      columns={columns}
      data={levelsOfCare?.data}
      sortingState={sortingState}
      errored={isLevelsOfCareError}
      emptyState={!levelsOfCare?.data?.length && !isLevelsOfCareLoading}
      pagination={{
        currentPage,
        totalPages: levelsOfCare?.meta.totalPages || 1,
        onPageChange: (page) => setCurrentPage(page),
      }}
      loading={isLevelsOfCareLoading}
      onHoverEnd={onHoverEnd}
      onRowHover={onRowHover}
      onSortingChange={setSortingState}
    />
  );
};
